import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Globe,
  ShieldCheck,
  Zap,
  Code2,
  Smartphone,
  TrendingUp,
  Clock,
  Play,
  Pause,
  Fingerprint,
  Users,
  CreditCard,
  UserCheck,
  Award,
  Settings,
  HeartHandshake,
  Receipt,
  Headphones,
  BarChart2,
  DollarSign,
  Building,
  Plug,
  Loader2,
} from 'lucide-react';
import SEO, { BASE_URL, FULL_NAME, LOGO_URL, SITE_NAME, SOCIAL_LINKS } from './seo/SEO';
import { db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { sendNotificationEmail } from './utils/emailService';

// --- HERO SLIDES (Auto-switches every 3 seconds) ---
const heroSlides = [
  {
    image: '/hero-1.png',
    eyebrow: 'YOUR GROWTH PARTNER • GLOBAL EPRC',
    title: (
      <>
        Your idea <span className="text-amber-400">+</span><br /> our expertise =<br /> <span className="text-amber-400">Success.</span>
      </>
    ),
    text: 'From recruitment to technology, we help businesses build teams, create digital solutions and grow globally.',
    note: 'G-Track 360 · Web Portals · Mobile Apps · Custom Software',
    badge: 'Enterprise IT Solutions',
    screenTitle: 'Admin / HR Web Panel',
  },
  {
    image: '/hero-2.png',
    eyebrow: 'FLAGSHIP ENTERPRISE PRODUCT • G-TRACK 360',
    title: (
      <>
        All-in-one HR & <span className="text-amber-400">Attendance platform.</span>
      </>
    ),
    text: 'Smarter attendance, safer workplaces, and complete HR management across web & mobile. Real-time geofencing, selfie verify, and automated payroll in 24 hours.',
    note: 'Live Attendance · Automated Payroll · Geo-Fencing & Biometrics',
    badge: 'Smart Workplace Platform',
    screenTitle: 'Employee Mobile Attendance App',
  },
  {
    image: '/hero-3.png',
    eyebrow: 'BUILT FOR TEAMS READY TO GROW',
    title: (
      <>
        Great products start with <span className="text-amber-400">clear thinking.</span>
      </>
    ),
    text: 'Work with a single partner across product design, development, cloud, and the skilled teams that bring your technical roadmap to life with zero-risk milestones.',
    note: 'Product teams · Cloud services · IT engineering squads',
    badge: 'Elite Engineering Teams',
    screenTitle: 'Manager Workforce Roster',
  },
  {
    image: '/hero-4.png',
    eyebrow: 'READY TO DOMINATE? BUILD YOUR EMPIRE',
    title: (
      <>
        Turn bold ideas into <span className="text-amber-400">lasting growth.</span>
      </>
    ),
    text: 'From enterprise ERPs and multi-branch data synchronization to high-converting performance marketing funnels that deliver measurable ROI.',
    note: 'Meta & Google Ads · B2B Lead Funnels · SEO Dominance',
    badge: 'Full-Funnel Advantage',
    screenTitle: 'Real-time Payroll & Tax Engine',
  },
];

// --- 4 CORE IT SERVICES (From flyer) ---
const coreItServices = [
  {
    number: '01',
    slug: 'web-solutions',
    category: '1. Web Solutions',
    badge: 'Web & SaaS',
    image: '/service-1.png',
    imageAlt: 'Web platform development',
    icon: Globe,
    badgeColor: 'bg-blue-600 text-white',
    accentBorder: 'hover:border-blue-500',
    summary: 'High-converting web platforms and custom portals built for speed, security, and enterprise scale.',
    features: [
      'Custom Web Portals & SaaS',
      'High-Converting Landing Pages',
      'Modern Responsive Websites',
      'Enterprise E-Commerce & APIs',
    ],
    tech: ['React', 'Next.js', 'Tailwind', 'Node.js'],
  },
  {
    number: '02',
    slug: 'mobile-apps',
    category: '2. Mobile Apps',
    badge: 'iOS & Android',
    image: '/service-2.png',
    imageAlt: 'Mobile workforce technology',
    icon: Smartphone,
    badgeColor: 'bg-emerald-600 text-white',
    accentBorder: 'hover:border-emerald-500',
    summary: 'Native and hybrid mobile applications designed with intuitive UX and offline synchronization.',
    features: [
      'Native & Hybrid Apps (iOS & Android)',
      'Intuitive & Modern UI/UX',
      'App Store & Play Store Launch',
      'Push Notifications & Offline Cache',
    ],
    tech: ['Flutter', 'React Native', 'Swift', 'Kotlin'],
  },
  {
    number: '03',
    slug: 'desktop-software',
    category: '3. Desktop Software',
    badge: 'ERP & Billing',
    image: '/service-3.png',
    imageAlt: 'Business software and payroll analytics',
    icon: Code2,
    badgeColor: 'bg-purple-600 text-white',
    accentBorder: 'hover:border-purple-500',
    summary: 'Centralized business operating software, custom ERPs, and automated point-of-sale systems.',
    features: [
      'Custom ERP & CRM Systems',
      'Integrated POS & Billing',
      'Automated Data Sync Across Branches',
      'Role-Based Security & Permissions',
    ],
    tech: ['Python', 'Electron', 'PostgreSQL', 'Microservices'],
  },
  {
    number: '04',
    slug: 'digital-marketing',
    category: '4. Digital Marketing',
    badge: 'Growth & Leads',
    image: '/service-4.png',
    imageAlt: 'Business team planning a growth strategy',
    icon: TrendingUp,
    badgeColor: 'bg-orange-600 text-white',
    accentBorder: 'hover:border-orange-500',
    summary: 'Data-driven marketing campaigns and technical SEO strategies that generate qualified B2B leads.',
    features: [
      'Meta & Google Ad Campaigns',
      'Local SEO & Top Rankings',
      'Qualified B2B Leads',
      'Brand Growth & Visibility',
    ],
    tech: ['Google Ads', 'Meta Ads', 'GA4', 'Technical SEO'],
  },
];

// --- COMPLETE G-TRACK 360 PRODUCT MODULES (From Image 3) ---
const gTrackModules = [
  {
    id: 'core-hr',
    category: 'Core HR & People',
    icon: Users,
    color: 'bg-blue-600 text-white',
    lightBg: 'border-blue-200 bg-blue-50/40',
    title: 'Core HR',
    subtitle: 'Build your people foundation',
    features: [
      'Employee Management & Directory',
      'Organization Structure & Hierarchy',
      'Company Policy & Handbook Hub',
      'Documents & Records Vault',
      'Complete Employee Lifecycle',
      'Transfers & Promotions Workflows',
      'Exit & Separation Management',
      'Configurable Workflows & Approvals',
      'Employee Self-Service (ESS) Portal',
    ],
  },
  {
    id: 'attendance',
    category: 'Core HR & People',
    icon: Clock,
    color: 'bg-emerald-600 text-white',
    lightBg: 'border-emerald-200 bg-emerald-50/40',
    title: 'Attendance Management',
    subtitle: 'Track time, boost productivity',
    features: [
      'Live GPS Attendance Tracking',
      'Geofence & Site Location Radius',
      'Selfie & Biometric Verification',
      'Multi-Shift Rostering & Rotations',
      'Automated Geo-Tagging on Punch',
      'Custom Holiday Calendar',
      'Weekly Offs & Overtime Computation',
      'Remote Work & Field Check-ins',
      'Automated Daily Attendance Reports',
    ],
  },
  {
    id: 'payroll',
    category: 'Payroll & Finance',
    icon: CreditCard,
    color: 'bg-purple-600 text-white',
    lightBg: 'border-purple-200 bg-purple-50/40',
    title: 'Payroll & Finance',
    subtitle: 'Accurate, compliant & hassle-free',
    features: [
      '1-Click Automated Payroll Processing',
      'CTC & Custom Salary Structures',
      'Automated Tax Calculation (TDS)',
      'Statutory Compliance (EPF, ESIC, PT)',
      'Employee Reimbursements & Claims',
      'Staff Loans & Salary Advances',
      'Arrears & Off-Cycle Disbursements',
      'Automated Digital Payslips (PDF/SMS)',
      'Bank Disbursal & Audit Reports',
    ],
  },
  {
    id: 'recruitment',
    category: 'Talent & Growth',
    icon: UserCheck,
    color: 'bg-orange-600 text-white',
    lightBg: 'border-orange-200 bg-orange-50/40',
    title: 'Recruitment & ATS',
    subtitle: 'Find and hire the best',
    features: [
      'Internal Job Requisitions',
      'Applicant Tracking System (ATS)',
      'Centralized Candidate Database',
      'AI Resume Parsing & Scoring',
      'Interview Scheduling & Feedbacks',
      'Offer Letter Approvals Workflow',
      'Digital E-Sign Offer Letters',
      'Self-Service Candidate Portal',
      'Recruitment Funnel Analytics',
    ],
  },
  {
    id: 'performance',
    category: 'Talent & Growth',
    icon: Award,
    color: 'bg-teal-600 text-white',
    lightBg: 'border-teal-200 bg-teal-50/40',
    title: 'Performance & OKRs',
    subtitle: 'Enable high performance',
    features: [
      'Goal Setting (OKRs & KPIs)',
      'Quarterly & Annual Reviews',
      'Continuous 1-on-1 Feedback',
      '360° Peer & Manager Feedback',
      'Performance Ratings & Normalization',
      'Individual Development Plans (IDP)',
      'Manager Evaluation Dashboards',
    ],
  },
  {
    id: 'operations',
    category: 'Operations & Engagement',
    icon: Settings,
    color: 'bg-amber-600 text-white',
    lightBg: 'border-amber-200 bg-amber-50/40',
    title: 'Operations & Learning',
    subtitle: 'Streamline daily operations',
    features: [
      'Employee Training Programs',
      'Skill Matrix & Gap Management',
      'Course Certifications Tracking',
      'Career Pathing & Milestones',
      'High-Potential Talent Development',
      'Succession Planning Matrix',
    ],
  },
  {
    id: 'engagement',
    category: 'Operations & Engagement',
    icon: HeartHandshake,
    color: 'bg-indigo-600 text-white',
    lightBg: 'border-indigo-200 bg-indigo-50/40',
    title: 'Employee Engagement',
    subtitle: 'A connected workforce',
    features: [
      'Pulse & eNPS Surveys',
      'Company Announcements & Alerts',
      'Department Communities & Groups',
      'Internal Social Feed & Shoutouts',
      'Company Events & Culture Calendar',
      'Peer Rewards & Spot Recognition',
      'Gamified Badges & Leaderboards',
    ],
  },
  {
    id: 'expense',
    category: 'Payroll & Finance',
    icon: Receipt,
    color: 'bg-sky-600 text-white',
    lightBg: 'border-sky-200 bg-sky-50/40',
    title: 'Expense & Travel',
    subtitle: 'Control and simplify spending',
    features: [
      'Mobile Expense Claims & Receipts',
      'Bill OCR & Auto-Scan Extraction',
      'Multi-Level Approval Workflows',
      'Corporate Travel Requests & Itineraries',
      'Custom Travel Policy Limits',
      'One-Click Reimbursement Payouts',
      'Departmental Expense Audits',
    ],
  },
  {
    id: 'helpdesk',
    category: 'Operations & Engagement',
    icon: Headphones,
    color: 'bg-pink-600 text-white',
    lightBg: 'border-pink-200 bg-pink-50/40',
    title: 'HR Helpdesk',
    subtitle: 'Support employees efficiently',
    features: [
      'Employee Inquiries & HR Ticketing',
      'Smart Ticket Categorization & Routing',
      'Interactive Knowledge Base & FAQs',
      'Automated Canned Responses',
      'SLA Tracking & Resolution Alerts',
      'Satisfaction Ratings & Helpdesk Reports',
    ],
  },
  {
    id: 'analytics',
    category: 'Platform & Intelligence',
    icon: BarChart2,
    color: 'bg-violet-600 text-white',
    lightBg: 'border-violet-200 bg-violet-50/40',
    title: 'People Analytics',
    subtitle: 'Data-driven decisions',
    features: [
      'Executive HR Dashboards',
      'Workforce Headcount & Attrition',
      'Custom Report Builder & Export',
      'Attendance & Overtime Trends',
      'Predictive Turnover Insights',
      'Compliance & Audit-Ready Reports',
      'Visual Charts & Exportable Data',
    ],
  },
  {
    id: 'compensation',
    category: 'Payroll & Finance',
    icon: DollarSign,
    color: 'bg-emerald-700 text-white',
    lightBg: 'border-emerald-200 bg-emerald-50/40',
    title: 'Compensation Management',
    subtitle: 'Plan, reward and retain',
    features: [
      'Merit & Annual Salary Reviews',
      'Compensation Benchmarking',
      'Department Budget Allocations',
      'Total Rewards Statements',
      'Bonus & Incentive Modeling',
      'Compensation Equity Analytics',
    ],
  },
  {
    id: 'organization',
    category: 'Core HR & People',
    icon: Building,
    color: 'bg-cyan-600 text-white',
    lightBg: 'border-cyan-200 bg-cyan-50/40',
    title: 'Organization Management',
    subtitle: 'Align people and structure',
    features: [
      'Dynamic Organizational Hierarchy',
      'Department & Cost Center Mapping',
      'Position & Role Architecture',
      'Reporting Line Visualization',
      'Restructuring Simulation & Moves',
      'Multi-Company Entity Support',
    ],
  },
  {
    id: 'integration',
    category: 'Platform & Intelligence',
    icon: Plug,
    color: 'bg-rose-600 text-white',
    lightBg: 'border-rose-200 bg-rose-50/40',
    title: 'Platform & Integration',
    subtitle: 'Built for enterprise scalability',
    features: [
      'Native iOS & Android Mobile Apps',
      'Multi-Language & Multi-Currency',
      'Open RESTful APIs & Webhooks',
      'Microsoft Teams & Slack Integration',
      'No-Code Custom Workflow Builder',
      'Role-Based Access Control (RBAC)',
      'Enterprise 256-Bit Data Encryption',
      'AI Attendance & Workflow Assistant',
    ],
  },
];

// --- 4 SCREENS SHOWCASE (From Bottom of Image 3) ---
const screenPanels = [
  {
    id: 'admin',
    name: 'Admin / HR Web Panel',
    desc: 'Complete control and insights: Manage employee rosters, geofenced branches, real-time check-ins, and one-click payroll disbursal.',
    badge: 'Desktop Web App',
    stats: [
      { label: 'Active Staff', value: '1,248' },
      { label: 'On-Time Rate', value: '98.4%' },
      { label: 'Payroll Ready', value: '100%' },
    ],
  },
  {
    id: 'employee',
    name: 'Employee Mobile App',
    desc: 'Everything at your fingertips: GPS geo-fenced selfie punch-in, real-time shift views, leave applications, and digital payslip downloads.',
    badge: 'iOS & Android App',
    stats: [
      { label: 'Punch Speed', value: '< 0.5s' },
      { label: 'Biometrics', value: 'Face + GPS' },
      { label: 'Offline Mode', value: 'Enabled' },
    ],
  },
  {
    id: 'manager',
    name: 'Manager Mobile App',
    desc: 'Manage your team on the go: Instant leave approvals, team shift swapping, live attendance roster, and overtime validation.',
    badge: 'Supervisor Portal',
    stats: [
      { label: 'Daily Average', value: '8h 10m' },
      { label: 'Pending Approvals', value: '0' },
      { label: 'Live Sites', value: '14 Active' },
    ],
  },
  {
    id: 'member',
    name: 'Team Member Live Punch',
    desc: 'Stay informed, stay connected: Daily clock-in countdown timer, shift schedules, overtime tracker, and internal company announcements.',
    badge: 'Field & Remote',
    stats: [
      { label: 'Active Timer', value: '03:13:30' },
      { label: 'Check-In', value: '11:46 AM' },
      { label: 'Day Status', value: 'Complete' },
    ],
  },
];

// --- SOFTWARE ARCHITECTURE PIPELINE ---
const architectureFlow = [
  { step: '01', image: '/stages/stage-auth.svg', title: 'Authentication & Roles' },
  { step: '02', image: '/stages/stage-modules.svg', title: 'Core Modules' },
  { step: '03', image: '/stages/stage-workflow.svg', title: 'Workflow Engine' },
  { step: '04', image: '/stages/stage-notifications.svg', title: 'Notifications' },
  { step: '05', image: '/stages/stage-analytics.svg', title: 'Reports & Analytics' },
  { step: '06', image: '/stages/stage-integrations.svg', title: 'Integrations & APIs' },
  { step: '07', image: '/stages/stage-mobile.svg', title: 'Mobile Apps' },
];

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  const [activeModuleTab, setActiveModuleTab] = useState('All');
  const [selectedScreen, setSelectedScreen] = useState('admin');

  // Booking Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'G-Track 360 Full Live Demo',
    details: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hero Slider (3-second auto transition with progress bar)
  useEffect(() => {
    if (isPaused) return;

    const intervalTime = 2500;
    const tickRate = 50;
    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += tickRate;
      setSlideProgress((elapsed / intervalTime) * 100);

      if (elapsed >= intervalTime) {
        elapsed = 0;
        setSlideProgress(0);
        setActiveSlide((prev) => (prev + 1) % heroSlides.length);
      }
    }, tickRate);

    return () => clearInterval(timer);
  }, [isPaused, activeSlide]);

  const goToSlide = (idx) => {
    setActiveSlide(idx);
    setSlideProgress(0);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill out your Name, Email, and Phone number.');
      return;
    }
    setIsSubmitting(true);
    try {
      // 1. Store in Firebase DB
      await addDoc(collection(db, 'demos'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        details: formData.details || '',
        status: 'New',
        createdAt: serverTimestamp(),
        createdAtString: new Date().toLocaleString(),
      });

      // 2. Send notification email to both globaleprc@gmail.com and jebincocjebnjebin232@gmail.com
      await sendNotificationEmail({
        formType: 'Book A Free Live Demo',
        subject: `[Demo Request] ${formData.name} - ${formData.service}`,
        data: {
          Name: formData.name,
          Email: formData.email,
          Phone: formData.phone,
          Service: formData.service,
          Details: formData.details || 'None provided',
        },
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting demo request to Firebase:', err);
      // Fallback: still show submitted
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToBooking = (serviceName = '') => {
    if (serviceName) {
      setFormData((prev) => ({ ...prev, service: serviceName }));
    }
    const elem = document.getElementById('demo-section');
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Filter modules by category
  const categories = ['All', 'Core HR & People', 'Payroll & Finance', 'Talent & Growth', 'Operations & Engagement', 'Platform & Intelligence'];
  const filteredModules = activeModuleTab === 'All'
    ? gTrackModules
    : gTrackModules.filter((m) => m.category === activeModuleTab);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: ['Global EPRC', 'GEPRC', FULL_NAME],
    url: `${BASE_URL}/`,
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: FULL_NAME,
    alternateName: SITE_NAME,
    url: `${BASE_URL}/`,
    logo: LOGO_URL,
    email: 'globaleprc@gmail.com',
    telephone: '+91-8610452001',
    sameAs: [
      SOCIAL_LINKS.linkedin,
      SOCIAL_LINKS.facebook,
      SOCIAL_LINKS.instagram,
      SOCIAL_LINKS.youtube,
    ],
  };

  const currentSlide = heroSlides[activeSlide];
  const activePanel = screenPanels.find((p) => p.id === selectedScreen) || screenPanels[0];

  return (
    <div className="overflow-x-hidden bg-[#f8fafc] text-slate-900 selection:bg-amber-400 selection:text-slate-950 font-sans">
      <SEO
        title="GEPRC | Global EPRC - High-Performance IT Solutions & G-Track 360"
        description="Scale faster, spend smarter. GlobalEPRC delivers G-Track 360 All-in-One HR & Attendance Platform, custom web portals, mobile apps, and enterprise software."
        path="/"
        jsonLd={[websiteSchema, organizationSchema]}
      />

      {/* ========================================================================= */}
      {/* 1. HERO SECTION (BOTH SIDES VISIBLE WITH 3-SECOND BACKGROUND SLIDER) */}
      {/* ========================================================================= */}
      <section
        id="home"
        className="relative isolate min-h-[640px] lg:min-h-[680px] overflow-hidden bg-slate-950 pt-24 text-white flex flex-col justify-between"
      >
        {/* Background Slides with crossfade and subtle zoom */}
        {heroSlides.map((slide, index) => (
          <div
            key={slide.image}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === activeSlide ? 'opacity-100 z-0' : 'opacity-0 -z-10'
            }`}
            aria-hidden={index !== activeSlide}
          >
            <img
              src={slide.image}
              alt=""
              className={`h-full w-full object-cover object-center transform transition-transform duration-[4000ms] ease-out ${
                index === activeSlide ? 'scale-105' : 'scale-100'
              }`}
            />
          </div>
        ))}

        {/* Ambient Grid lines */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:3.5rem_3.5rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

        {/* Hero Content Container: BOTH SIDES VISIBLE */}
        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 py-12 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            
            {/* === LEFT SIDE: HEADLINE, COPY & ACTIONS === */}
            <div className="lg:col-span-8">
              {/* Eyebrow badge */}
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-amber-300 backdrop-blur-md">
                <span>{currentSlide.eyebrow}</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.2rem] font-black leading-[1.05] tracking-[-0.04em] text-white">
                {currentSlide.title}
              </h1>

              {/* Sub-paragraph */}
              <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-200">
                {currentSlide.text}
              </p>

              {/* 3 Key Trust Bullet Points */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 text-xs font-semibold text-slate-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                  <span>Smart Geo-Attendance</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-amber-400 shrink-0" />
                  <span>Automated Payroll & Tax</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0" />
                  <span>24-Hr Rapid Setup</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="button"
                  onClick={() => scrollToBooking('G-Track 360 Live Demo')}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-7 py-3.5 text-sm font-black text-slate-950 shadow-lg shadow-amber-400/25 transition-all hover:bg-amber-300 hover:scale-[1.02]"
                >
                  <span>Book Free Live Demo</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>

                <a
                  href="#g-track-details"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 px-6 py-3.5 text-sm font-bold text-white backdrop-blur-md transition hover:bg-white/20 hover:border-white/40"
                >
                  Explore G-Track 360
                  <ChevronRight className="h-4 w-4" />
                </a>

              </div>
            </div>

            {/* === RIGHT SIDE: LIVE PRODUCT UI MOCKUP PREVIEW (ALWAYS VISIBLE!) === */}
            <div className="hidden lg:col-span-5">
              <div className="relative rounded-3xl border border-white/20 bg-slate-900/90 p-5 shadow-2xl backdrop-blur-2xl ring-1 ring-white/10">
                {/* Browser window chrome */}
                <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-500" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                    <span className="ml-2 font-mono text-[11px] text-slate-400">
                      app.gtrack360.com/live
                    </span>
                  </div>
                  <span className="rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-[10px] font-bold text-emerald-300 border border-emerald-500/30">
                    ● Real-Time Sync
                  </span>
                </div>

                {/* Interactive Screen Preview */}
                <div className="space-y-3">
                  <div className="rounded-2xl border border-white/10 bg-slate-950 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white font-bold">
                          <Fingerprint className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white">G-Track 360 Control Center</p>
                          <p className="text-[10px] text-slate-400">Live Attendance & Workforce Roster</p>
                        </div>
                      </div>
                      <span className="text-[10px] font-black uppercase text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/20">
                        {currentSlide.screenTitle}
                      </span>
                    </div>

                    {/* Live KPIs */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-xl bg-white/5 p-2.5 text-center border border-white/5">
                        <p className="text-[10px] text-slate-400">Active Staff</p>
                        <p className="text-lg font-black text-white">1,248</p>
                        <p className="text-[9px] text-emerald-400">98.4% On Duty</p>
                      </div>
                      <div className="rounded-xl bg-white/5 p-2.5 text-center border border-white/5">
                        <p className="text-[10px] text-slate-400">Geofence Sites</p>
                        <p className="text-lg font-black text-amber-400">14</p>
                        <p className="text-[9px] text-slate-300">GPS Verified</p>
                      </div>
                      <div className="rounded-xl bg-white/5 p-2.5 text-center border border-white/5">
                        <p className="text-[10px] text-slate-400">Payroll Cycle</p>
                        <p className="text-lg font-black text-blue-400">100%</p>
                        <p className="text-[9px] text-slate-300">TDS Computed</p>
                      </div>
                    </div>

                    {/* Live feed item */}
                    <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-2.5 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-slate-300 font-medium">Biometric punch verified at Bangalore HQ</span>
                      </div>
                      <span className="text-[10px] text-amber-300 font-mono">0.3s ago</span>
                    </div>
                  </div>

                  {/* 3 Quick Switch Pills */}
                  <div className="grid grid-cols-3 gap-2 text-center text-[11px] font-bold">
                    <div className="rounded-xl bg-blue-600/20 border border-blue-500/40 p-2 text-blue-300">
                      Smart Attendance
                    </div>
                    <div className="rounded-xl bg-emerald-600/20 border border-emerald-500/40 p-2 text-emerald-300">
                      Auto Payroll
                    </div>
                    <div className="rounded-xl bg-purple-600/20 border border-purple-500/40 p-2 text-purple-300">
                      24-Hr Setup
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Bottom Slider Navigation & 3-Second Timer Controls */}
        <div className="relative z-20 border-t border-white/10 bg-slate-950/85 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl flex-col items-stretch justify-between gap-3 px-5 py-3 sm:flex-row sm:items-center sm:px-8 lg:px-10">
            {/* Left: Banner motto */}
            <div className="hidden flex items-center gap-2.5 text-xs text-slate-300 font-medium">
              <ShieldCheck className="h-4 w-4 text-amber-400 shrink-0" />
              <span>Full-Funnel Advantage · Enterprise Grade Tech (99.9% Uptime) · Zero-Risk Delivery</span>
            </div>
            <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                ['Enterprise Grade Tech', '99.9% uptime'],
                ['Zero-Risk Delivery', 'Milestone-based'],
                ['Proven ROI & Scalability', 'Results that grow'],
                ['24-Hr Rapid Setup', 'Get started faster'],
              ].map(([title, detail]) => <div key={title} className="flex items-start gap-2 text-[9px] font-semibold text-slate-300 sm:text-[10px]"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" /><span><strong className="block text-white">{title}</strong><span className="text-slate-400">{detail}</span></span></div>)}
            </div>

            {/* Right: Slide Controls with Pause/Play */}
            <div className="flex items-center justify-between sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsPaused(!isPaused)}
                aria-label={isPaused ? 'Resume auto-slider' : 'Pause auto-slider'}
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-slate-300 hover:text-white hover:bg-white/10 transition"
              >
                {isPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goToSlide((activeSlide - 1 + heroSlides.length) % heroSlides.length)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-slate-300 hover:text-white hover:bg-white/10 transition"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>

                {heroSlides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goToSlide(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      activeSlide === i
                        ? 'w-8 bg-amber-400'
                        : 'w-2.5 bg-white/30 hover:bg-white/60'
                    }`}
                    aria-label={`Jump to slide ${i + 1}`}
                  />
                ))}

                <button
                  type="button"
                  onClick={() => goToSlide((activeSlide + 1) % heroSlides.length)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-slate-300 hover:text-white hover:bg-white/10 transition"
                  aria-label="Next slide"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="hidden sm:block w-16 h-1 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 transition-all duration-75 ease-linear rounded-full"
                  style={{ width: `${slideProgress}%` }}
                />
              </div>

              <span className="font-mono text-xs font-bold text-amber-400">
                0{activeSlide + 1} / 04
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3. CORE IT SERVICES SECTION (The 4 Cards from Flyer) */}
      {/* ========================================================================= */}
      <section id="services" className="relative overflow-hidden border-y border-slate-200 bg-[#f3f6fb] py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <div className="mb-10 flex flex-col gap-5 sm:mb-12 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">Technology for what’s next</p>
              <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-slate-950 sm:text-5xl">Core IT Solutions <span className="text-blue-700">Built to Grow.</span></h2>
            </div>
            <div className="flex max-w-md items-end gap-4">
              <p className="text-sm leading-relaxed text-slate-600">From your first digital product to enterprise-wide systems, we build the technology that moves your business forward.</p>
              <a href="#g-track-details" className="hidden shrink-0 items-center rounded-lg bg-slate-950 px-4 py-3 text-xs font-bold text-white transition hover:bg-blue-800 sm:inline-flex">Explore <ArrowRight className="ml-2 h-3.5 w-3.5" /></a>
            </div>
          </div>

          <div className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 xl:gap-6">
            {coreItServices.map((service) => {
              const targetUrl = service.slug === 'digital-marketing' ? '/digital-marketing' : `/services/${service.slug}`;
              return (
                <Link
                  key={service.category}
                  to={targetUrl}
                  className="group relative flex min-h-[300px] sm:min-h-[350px] min-w-0 flex-col justify-end overflow-hidden rounded-3xl bg-slate-950 shadow-lg transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl active:scale-[0.98] cursor-pointer block focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  <img 
                    src={service.image} 
                    alt={service.imageAlt} 
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" 
                  />
                  {/* High contrast gradient for crisp readability on mobile & desktop */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/60 to-slate-950/20 transition-colors duration-300 group-hover:via-slate-950/70" />
                  
                  <div className="relative z-10 p-6 sm:p-7 text-white flex flex-col justify-end">
                    <span className="mb-2.5 inline-flex self-start rounded-full bg-blue-500/20 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-blue-300 border border-blue-400/25 backdrop-blur-sm">
                      {service.badge}
                    </span>
                    <h3 className="text-2xl sm:text-3xl font-black leading-tight text-white group-hover:text-amber-300 transition-colors">
                      {service.category.replace(/^\d+\.\s*/, '')}
                    </h3>
                    <p className="mt-2 text-xs sm:text-sm leading-relaxed text-slate-200/90 line-clamp-2 sm:line-clamp-3">
                      {service.summary}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 text-xs sm:text-sm font-black text-amber-400 group-hover:text-amber-300 transition-colors">
                      <span>Learn more</span>
                      <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 4. COMPREHENSIVE G-TRACK 360 PRODUCT DETAILS */}
      {/* ========================================================================= */}
      <section id="g-track-details" className="relative overflow-hidden bg-white pb-16 text-slate-900 border-t border-slate-200 sm:pb-20">
        <div className="mb-0 bg-gradient-to-b from-slate-50/80 via-white to-white border-b border-slate-100">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 sm:px-8 md:grid-cols-2 lg:px-10 lg:py-20">
            <div>
              <p className="inline-flex rounded-full border border-amber-300 bg-amber-50 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.22em] text-amber-800">
                Flagship Enterprise Platform
              </p>
              <h2 className="mt-4 text-5xl font-black tracking-tight text-slate-950 sm:text-6xl">
                G-Track <span className="text-blue-600">360</span>
              </h2>
              <p className="mt-2 text-lg font-bold text-slate-800">
                All-in-One HR &amp; Attendance Management Platform
              </p>
              <p className="mt-2 text-xs font-black uppercase tracking-[0.2em] text-blue-600">
                Track / Monitor / Manage / Grow
              </p>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-600">
                A modern HR and attendance solution for your team, anytime, anywhere. Built for smarter attendance, safer workplaces, and complete workforce lifecycle management.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button 
                  type="button" 
                  onClick={() => scrollToBooking('G-Track 360 Full Live Demo')} 
                  className="rounded-xl bg-slate-950 px-6 py-3.5 text-xs font-black text-white shadow-md transition hover:bg-slate-800 flex items-center gap-2"
                >
                  Explore G-Track 360 <ArrowRight className="h-3.5 w-3.5" />
                </button>
                <button 
                  type="button" 
                  onClick={() => scrollToBooking('G-Track 360 Full Live Demo')} 
                  className="rounded-xl border border-slate-300 bg-white px-6 py-3.5 text-xs font-bold text-slate-800 shadow-xs transition hover:bg-slate-50 flex items-center gap-2"
                >
                  Request Demo <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
              <div className="relative rounded-t-2xl border border-slate-200 bg-white p-3 shadow-xl sm:p-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-rose-400" /><span className="h-2 w-2 rounded-full bg-amber-400" /><span className="h-2 w-2 rounded-full bg-emerald-400" /><span className="ml-2 text-[9px] font-bold text-slate-500">G-Track 360 Control Center</span></div>
                  <span className="rounded-full bg-emerald-50 px-2 py-1 text-[8px] font-bold text-emerald-700">Live dashboard</span>
                </div>
                <div className="grid grid-cols-[82px_1fr] gap-3 pt-3">
                  <div className="space-y-2 rounded-lg bg-slate-950 p-2 text-[8px] font-medium text-slate-300"><p className="mb-3 font-black text-white">G-TRACK</p><p className="rounded bg-blue-600 px-1.5 py-1 text-white">Overview</p><p>Employees</p><p>Attendance</p><p>Payroll</p><p>Reports</p></div>
                  <div className="min-w-0">
                    <div className="mb-2 flex items-center justify-between"><div><p className="text-[10px] font-black text-slate-900">Workforce overview</p><p className="text-[8px] text-slate-400">Welcome back, Admin</p></div><span className="rounded bg-blue-600 px-2 py-1 text-[8px] font-bold text-white">This month</span></div>
                    <div className="grid grid-cols-3 gap-1.5">{[['Employees', '1,248'], ['On time', '98.4%'], ['Payroll', 'Ready']].map(([label, value]) => <div key={label} className="rounded-md border border-slate-100 bg-slate-50 p-2"><p className="text-[7px] text-slate-400">{label}</p><p className="mt-1 text-[10px] font-black text-slate-900">{value}</p></div>)}</div>
                    <div className="mt-2 grid h-24 grid-cols-[1fr_92px] gap-2 rounded-md border border-slate-100 p-2"><div className="flex items-end justify-around gap-1 border-b border-slate-100 px-1">{[35, 55, 43, 72, 58, 88, 65, 96].map((height, index) => <span key={index} className="w-2 rounded-t bg-blue-500" style={{ height: `${height}%` }} />)}</div><div className="flex flex-col items-center justify-center rounded bg-blue-50"><span className="h-12 w-12 rounded-full border-[7px] border-blue-500 border-r-amber-300 border-b-emerald-400" /><span className="mt-1 text-[7px] text-slate-500">Attendance</span></div></div>
                  </div>
                </div>
              </div>
              <div className="mx-auto h-2 w-[92%] rounded-b-xl bg-slate-200 shadow-md" />
              <div className="absolute -bottom-2 right-2 w-[76px] rounded-[16px] border-[3px] border-slate-200 bg-white p-1.5 shadow-xl sm:-bottom-4 sm:right-1 sm:w-[90px]">
                <div className="rounded-[10px] bg-slate-50 px-2 py-3 text-center"><span className="mx-auto mb-2 block h-1 w-5 rounded bg-slate-300" /><span className="mx-auto flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-[10px] text-blue-700">✓</span><p className="mt-2 text-[7px] font-black text-slate-800">Good morning!</p><p className="mt-1 text-[6px] text-slate-500">You are checked in</p><div className="mt-2 rounded bg-blue-600 py-1 text-[6px] font-bold text-white">View attendance</div></div>
              </div>
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <div className="mb-10 mt-8">
            <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">Platform Capabilities</p>
                <h3 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-950">Key Modules</h3>
              </div>
              <p className="max-w-md text-xs leading-relaxed text-slate-600">One connected platform for people, payroll, attendance and day-to-day operations.</p>
            </div>
            {/* <div className="grid grid-cols-2 gap-2.5 sm:gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {gTrackModules.map((module) => {
                const Icon = module.icon;
                return (
                  <button
                    type="button"
                    key={module.id}
                    onClick={() => scrollToBooking(module.title)}
                    className="group flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 sm:p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-amber-400 hover:shadow-md active:scale-[0.97] cursor-pointer min-h-[124px] sm:min-h-[104px] justify-between sm:justify-start overflow-hidden w-full focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    <span className={`flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl shadow-sm transition-transform group-hover:scale-105 ${module.color}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 w-full">
                      <span className="block text-xs sm:text-sm font-black leading-snug text-slate-900 group-hover:text-blue-700 transition-colors break-words">
                        {module.title}
                      </span>
                      <span className="mt-1 block text-[10px] sm:text-[11px] leading-tight text-slate-500 line-clamp-2">
                        {module.subtitle}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div> */}
          </div>
          
          {/* Main G-Track Header from Image 3 */}
          <div className="hidden text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              <Fingerprint className="h-4 w-4 text-blue-600" />
              Flagship Enterprise Platform
            </div>

            <div className="mt-4 flex items-center justify-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-md">
                <Fingerprint className="h-7 w-7" />
              </div>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-950">
                G-Track <span className="text-blue-600">360</span>
              </h2>
            </div>

            <p className="mt-3 text-lg sm:text-xl font-bold text-slate-800">
              All-in-One HR & Attendance Management Platform
            </p>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-600 mt-1">
              Track • Monitor • Manage • Grow
            </p>
            <p className="mt-3 text-sm text-slate-600 max-w-2xl mx-auto">
              A modern HR & attendance solution for your team, anytime, anywhere. Built for smarter attendance, safer workplaces, and complete workforce lifecycle management.
            </p>
          </div>

          {/* Module Category Filter Tabs (Zoho Style) */}
          <div className="hidden mt-10 flex-wrap items-center justify-center gap-2">
            {categories.map((cat) => (
              <button
                type="button"
                key={cat}
                onClick={() => setActiveModuleTab(cat)}
                className={`rounded-xl px-4 py-2 text-xs font-bold transition-all ${
                  activeModuleTab === cat
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* The 13 Full Product Modules Grid */}
          <div className="hidden mt-10 grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredModules.map((m) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.id}
                  className={`rounded-2xl border p-5 transition-all duration-300 hover:shadow-lg ${m.lightBg}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-sm ${m.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">{m.title}</h3>
                      <p className="text-[10px] text-slate-500 font-medium">{m.subtitle}</p>
                    </div>
                  </div>

                  <ul className="mt-4 space-y-1.5 border-t border-slate-200/80 pt-3">
                    {m.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2 text-[11px] text-slate-700">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* ========================================================================= */}
          {/* MULTI-SCREEN INTERFACE SHOWCASE (Admin Web Panel + 3 Mobile Apps) */}
          {/* ========================================================================= */}
          <div className="mt-12 rounded-3xl border border-slate-200 bg-slate-50 p-5 text-slate-900 shadow-sm sm:p-8">
            <div className="max-w-2xl">
              <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-blue-700">
                Multi-Device Experience
              </span>
              <h3 className="mt-3 text-2xl font-black text-slate-950 sm:text-4xl">
                Engineered for Web, Android & iOS
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Give HR administrators complete visibility on desktop, while empowering employees and managers on mobile.
              </p>
            </div>

            {/* Screen Selector Buttons */}
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {screenPanels.map((panel) => (
                <button
                  type="button"
                  key={panel.id}
                  onClick={() => setSelectedScreen(panel.id)}
                  className={`rounded-xl p-3 text-left transition ${
                    selectedScreen === panel.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-slate-600 hover:bg-blue-50'
                  }`}
                >
                  <p className="text-xs font-bold">{panel.name}</p>
                  <p className="text-[10px] opacity-75">{panel.badge}</p>
                </button>
              ))}
            </div>

            {/* Active Panel Details */}
            <div className="mt-8 grid grid-cols-1 items-center gap-8 border-t border-slate-200 pt-8 lg:grid-cols-12">
              <div className="space-y-4 lg:col-span-5">
                <h4 className="text-xl font-bold text-slate-950">{activePanel.name}</h4>
                <p className="text-sm leading-relaxed text-slate-600">{activePanel.desc}</p>
                
                <div className="grid grid-cols-3 gap-3 pt-2">
                  {activePanel.stats.map((s) => (
                    <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-3 text-center">
                      <p className="text-[10px] text-slate-500">{s.label}</p>
                      <p className="mt-0.5 text-base font-black text-slate-950">{s.value}</p>
                    </div>
                  ))}
                </div>

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => scrollToBooking(activePanel.name)}
                    className="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-6 py-3 text-xs font-black text-slate-950 transition hover:bg-amber-300"
                  >
                    Request Live Walkthrough <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-3 text-xs font-mono text-slate-500">
                    <span>G-Track 360 UI Preview</span>
                    <span className="text-emerald-400">● 99.9% Uptime</span>
                  </div>
                  <img
                    src="/gtrack-360-preview.jpeg"
                    alt="G-Track 360 HR and attendance platform overview with admin and mobile app previews"
                    className="max-h-[500px] w-full rounded-xl object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ========================================================================= */}
          {/* HIGH-LEVEL SOFTWARE ARCHITECTURE PIPELINE */}
          {/* ========================================================================= */}
          <div className="mt-16 rounded-[2.5rem] border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-amber-50/20 p-6 sm:mt-20 sm:p-10 shadow-sm relative overflow-hidden">
            <div className="mb-8 flex flex-col gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-700 bg-amber-50 px-3 py-1 rounded-full border border-amber-200/60 inline-block">
                  Software Structure
                </span>
                <h3 className="mt-3 font-serif text-2xl font-bold leading-tight text-slate-950 sm:text-3xl">
                  How G-Track 360 works
                </h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
                  A connected flow from secure sign-in to useful workforce insights, available across web and mobile.
                </p>
              </div>
              <span className="inline-flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 shadow-xs">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                7 connected stages
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 sm:gap-4">
              {architectureFlow.map((flow) => (
                <article 
                  key={flow.step} 
                  onClick={() => {
                    document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="group relative flex flex-col items-center text-center rounded-2xl border border-slate-200/90 bg-white p-5 shadow-xs transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-amber-400 cursor-pointer overflow-hidden"
                >
                  {/* Top Row: Step Indicator & Dot */}
                  <div className="flex items-center justify-between w-full mb-2">
                    <span className="font-mono text-[10px] font-black tracking-widest px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 group-hover:bg-amber-100 group-hover:text-amber-800 transition-colors">
                      STEP {flow.step}
                    </span>
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 group-hover:bg-amber-500 transition-colors" />
                  </div>

                  {/* Central Transparent 3D Stage Image */}
                  <div className="relative flex h-20 w-20 items-center justify-center my-2 transition-transform duration-300 group-hover:scale-110">
                    <img 
                      src={flow.image} 
                      alt={flow.title} 
                      className="h-full w-full object-contain filter drop-shadow-sm select-none pointer-events-none" 
                      loading="lazy"
                    />
                  </div>

                  {/* Stage Title Only (No Description) */}
                  <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 group-hover:text-amber-600 transition-colors leading-snug flex items-center justify-center min-h-[36px]">
                    {flow.title}
                  </h4>

                  {/* Bottom Hover Accent Bar */}
                  <span className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 transition-transform duration-300 group-hover:scale-x-100" />
                </article>
              ))}
            </div>
          </div>

          {/* The 3 Guarantees from Image 2 */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-slate-200 pt-10">
            <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
                <Zap className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">Full-Funnel Advantage</h4>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">Full Build, We Scale, Deliver ROI!</p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">Enterprise Grade Tech</h4>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">Robust, Secure & 99.9% Uptime backed by cloud SLAs.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-600 text-white">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">Zero-Risk Delivery</h4>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">Milestone-Based Sprints & Dedicated Technical Support.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 5. READY TO DOMINATE? CONVERSION & DEMO SECTION (From Flyer) */}
      {/* ========================================================================= */}
      <section id="demo-section" className="relative bg-slate-950 py-16 text-white sm:py-20" style={{ backgroundImage: "linear-gradient(90deg, rgba(2,6,23,.96), rgba(2,6,23,.78)), url('/it.jpeg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            {/* Left Column: Trust points */}
            <div className="lg:col-span-6">
              <span className="rounded-full bg-amber-400/10 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-300 border border-amber-400/30">
                Your Growth Partner
              </span>
              <h2 className="mt-4 text-3xl sm:text-5xl font-black text-white leading-tight">
                READY TO DOMINATE? <br />
                <span className="text-amber-400">Let’s Build Your Future Empire!</span>
              </h2>
              
              <div className="mt-3 inline-block rounded-lg bg-white/10 px-3 py-1.5 text-xs font-bold text-amber-300 border border-white/10">
                Your Idea + Our Expertise = Success
              </div>

              <div className="mt-6 space-y-3">
                {[
                  'Proven ROI & Scalability for fast-growing businesses',
                  'Enterprise-Grade Security & 99.9% Uptime Architecture',
                  'Zero-Risk Delivery Model with Milestone sprint reviews',
                  'Premium Talent & Tech across Web, Mobile & Software',
                ].map((pt) => (
                  <div key={pt} className="flex items-center gap-3 text-sm text-slate-200">
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-400 text-slate-950 font-black text-[10px]">
                      ✓
                    </div>
                    <span>{pt}</span>
                  </div>
                ))}
              </div>

              {/* Direct Contacts box */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a
                  href="https://wa.me/918610452001?text=Hi%20GlobalEPRC%2C%20I%20would%20like%20to%20book%20a%20free%20live%20demo."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 transition hover:bg-emerald-500/20"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">WhatsApp Text</p>
                    <span className="text-[10px] bg-emerald-500/30 text-emerald-200 px-2 py-0.5 rounded-full font-bold">Text Us Here</span>
                  </div>
                  <p className="text-base font-black text-white mt-1">+91 8610452001</p>
                  <p className="text-[11px] text-emerald-300/80 mt-1">Click to text us on WhatsApp</p>
                </a>

                <a
                  href="mailto:globaleprc@gmail.com"
                  className="rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4 transition hover:bg-blue-500/20"
                >
                  <p className="text-[10px] font-bold text-blue-300 uppercase tracking-wider">Direct Email</p>
                  <p className="text-base font-black text-white mt-1">globaleprc@gmail.com</p>
                  <p className="text-[11px] text-blue-300/80 mt-1">Inquiries & support anytime</p>
                </a>
              </div>
            </div>

            {/* Right Column: Live Booking Form */}
            <div className="lg:col-span-6">
              <div className="rounded-3xl border border-slate-800 bg-slate-900 p-5 sm:p-8 md:p-9 shadow-2xl">
                <h3 className="text-2xl font-bold text-white">Book A Free Live Demo Now!</h3>
                <p className="mt-1 text-xs text-slate-400">
                  Experience G-Track 360 live or discuss your custom web/app software requirement.
                </p>

                {submitted ? (
                  <div className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center">
                    <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
                    <p className="text-base font-bold text-white">Demo Request Submitted!</p>
                    <p className="text-xs text-slate-300 mt-1">
                      Your details have been saved. Our specialist will reach out to you shortly.
                    </p>
                    <a
                      href={`https://wa.me/918610452001?text=Hi%20GlobalEPRC%2C%20I%20just%20submitted%20a%20demo%20request%20for%20${encodeURIComponent(formData.service)}.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-emerald-600"
                    >
                      Text Us on WhatsApp (+91 8610452001) →
                    </a>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="mt-6 space-y-4">
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300 mb-1">
                        Select Solution:
                      </label>
                      <select
                        value={formData.service}
                        onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                        className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3.5 py-2.5 text-xs text-white focus:border-amber-400 focus:outline-none"
                      >
                        <option>G-Track 360 Full Live Demo</option>
                        <option>Web Solutions & SaaS Portal</option>
                        <option>Mobile App Development (iOS & Android)</option>
                        <option>Desktop Software & Custom ERP</option>
                        <option>Digital Marketing & SEO</option>
                        <option>Manpower & Recruitment Services</option>
                        <option>HR Outsourcing Solutions</option>
                        <option>Skill Development & Training</option>
                        <option>Travel & Tourism Services</option>
                        <option>Others (Custom Requirement)</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Your Name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300 mb-1">
                          Work Email *
                        </label>
                        <input
                          type="email"
                          required
                          placeholder="name@company.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300 mb-1">
                        Phone / WhatsApp *
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-300 mb-1">
                        Requirements (Optional)
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Team size, current tools, or specific requirements..."
                        value={formData.details}
                        onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                        className="w-full rounded-xl border border-slate-700 bg-slate-800 p-3 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full rounded-xl bg-amber-400 px-5 py-3 text-sm font-black text-slate-950 shadow-md transition hover:bg-amber-300 disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Submitting Request...
                        </>
                      ) : (
                        'BOOK A FREE LIVE DEMO NOW! →'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
